/**
 * Runtime configuration utilities for managing environment variables
 * that need to be accessible on both server and client sides.
 *
 * This module provides a way to safely expose NEXT_PUBLIC_ environment
 * variables to the client-side code while maintaining type safety.
 */

/** Cached server-side  to avoid repeated environment variable collection */
let cachedServerRuntimeConfig: Record<string, string> | null = null;

/** Cached client-side configuration to avoid repeated JSON parsing */
let cachedClientRuntimeConfig: Record<string, string> | null = null;

/**
 * Clear the cache for both server-side and client-side runtime configurations
 * This is useful for testing or when you want to refresh the config
 * without restarting the server or reloading the page
 *
 * @returns void
 */
export function clearRuntimeConfigCache() {
  cachedServerRuntimeConfig = null;
  cachedClientRuntimeConfig = null;
}

/**
 * Collects and serves runtime configuration from environment variables.
 * This function can only be called on the server side and caches the
 * configuration for performance.
 *
 * @returns Record containing all NEXT_PUBLIC_ environment variables plus NODE_ENV
 * @throws Error if called on the client side
 */
export function servePublicRuntimeConfig() {
  if (typeof document !== 'undefined') {
    throw new Error(
      'servePublicRuntimeConfig can only be called on the server side'
    );
  }

  if (
    cachedServerRuntimeConfig !== null &&
    process.env.NODE_ENV === 'production'
  ) {
    return cachedServerRuntimeConfig;
  }

  cachedServerRuntimeConfig = {};

  // Collect all NEXT_PUBLIC_ environment variables that are currently set
  for (const [key, value] of Object.entries(process.env)) {
    if (
      key.startsWith('NEXT_PUBLIC_') &&
      value !== undefined &&
      value !== null
    ) {
      cachedServerRuntimeConfig[key] = value;
    }
  }

  // Add NODE_ENV to the runtime config as a special case matching existing behavior
  if (process.env.NODE_ENV !== undefined) {
    cachedServerRuntimeConfig.NODE_ENV = process.env.NODE_ENV;
  }

  return cachedServerRuntimeConfig;
}

/**
 * Retrieves a specific configuration value by key.
 * Automatically handles the difference between server-side and client-side execution.
 *
 * @param key - The configuration key to retrieve (e.g., 'NEXT_PUBLIC_API_URL')
 * @returns The configuration value, or undefined if not found
 */
export function getPublicRuntimeConfig(key: string) {
  if (
    typeof document === 'undefined' ||
    (typeof document !== 'undefined' && process.env.NODE_ENV === 'test')
  ) {
    // Catch server-side NODE_ENV here, enabling this to work in compiled
    // Temporal workflow code and other custom-compiled environments.
    if (key === 'NODE_ENV') {
      // Webpack / ESBuild will replace this at build time
      return process.env.NODE_ENV;
    }
    // Server-side or test environment: access the environment variables directly
    // Certain tests assign mock values to document, so we can't use it to determine
    // if we're on the server
    return process.env[key];
  } else {
    // Client-side: use cached config or parse from script block once
    if (cachedClientRuntimeConfig === null) {
      const configScript = document.getElementById('public-runtime-config');
      if (configScript && configScript.textContent) {
        try {
          cachedClientRuntimeConfig = JSON.parse(configScript.textContent);
        } catch (error) {
          console.error('Failed to parse runtime config:', error);
          // Leave cachedClientRuntimeConfig as null to trigger a retry on next call
        }
      }
    }
    return cachedClientRuntimeConfig?.[key];
  }
}
